/**
 * Authentication RTK Query API
 *
 * Defines RTK Query endpoints for authentication operations.
 * Integrates with TravelCRM.API POST /api/auth/login endpoint.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '../types/auth.types';
import { resolveAuthApiUrl } from '../../../Api/apiUrl';

// Auth API uses base /api route (not /api/admin).
// We derive it from REACT_APP_API_URL to support production builds.
const API_URL = resolveAuthApiUrl(process.env.REACT_APP_API_URL);

/**
 * Base query configuration for auth API
 * Includes JWT token in Authorization header for authenticated requests
 */
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Auth API service with login endpoint
 * Tag: 'Auth' for cache invalidation
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    /**
     * Login endpoint
     * POST /api/auth/login
     * Accepts email and password, returns JWT token and user info
     */
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),

    /**
     * Logout endpoint (optional, for future use)
     * Can be used to invalidate token on backend
     */
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),

    /**
     * Change password endpoint
     * POST /api/auth/change-password
     * Accepts current password, new password, and optional reset token
     * Forces logout after success (no token refresh)
     */
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: (data) => ({
        url: '/auth/change-password',
        method: 'POST',
        body: data,
      }),
      // No invalidatesTags - force logout handled in component
    }),
  }),
});

/**
 * Export hooks for use in components
 * Alias hooks to avoid generic naming conflicts between features
 */
export const { useLoginMutation, useLogoutMutation, useChangePasswordMutation } = authApi;
